import React from "react";
import { Auth } from "../helpers/auth";
import CrIcon from "./CrIcon";

// TIER 1 of two invite paths, and the one only Eric has.
//
// This card starts a WHOLE NEW CUSTOMER: POST /auth/admin/invites creates the
// owner account for a client company and emails them a link to set their
// password. It never adds anybody to the workspace you are looking at -- that
// is Team Directory's "Invite" action (POST /auth/workspace/invites), which
// every client's own owner or admin can use inside their own workspace.
//
// It lives on its own page (pages/ClientAccounts.js, route /clients) rather
// than in the Control Room, so BossDrop's own admin work never shares a screen
// with a client's workspace settings.
//
// Signups are closed (SIGNUPS_ENABLED is off on the server), so a new client
// gets their account from here. The server checks PLATFORM_ADMIN_EMAILS on its
// own; the page and the route both check isPlatformAdmin from /auth/me before
// this card is drawn, so nobody else even sees that it exists.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function errorMessage(code, email) {
  const who = email || "That address";
  switch (code) {
    case "already_has_account":
      return `${who} already has a BossDrop account, so there is nothing to create.`;
    case "is_teammate":
      return `${who} is already on a client's team, so they can't be set up as the owner of a new client.`;
    case "invalid_email":
      return "That doesn't look like an email address.";
    case "forbidden":
      return "This account isn't allowed to create client accounts.";
    case "unauthorized":
      return "Your session expired. Sign in again, then create the account.";
    case "email_failed":
      return "The account was created but the email didn't send. Submit it again to resend the link.";
    default:
      return "The account wasn't created. Try again in a minute.";
  }
}

// `onInvited` runs after an invite goes out (the page reloads its client list).
export default function CreateClientAccountCard({ onInvited } = {}) {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(null); // { email, resent }
  const [error, setError] = React.useState("");

  const trimmedEmail = email.trim();
  const canSend = !sending && !!trimmedEmail;

  async function send(e) {
    e.preventDefault();
    if (!canSend) return;
    if (!EMAIL_RE.test(trimmedEmail)) {
      setSent(null);
      setError(errorMessage("invalid_email"));
      return;
    }
    setSending(true);
    setError("");
    // The last confirmation should not sit under a new submission.
    setSent(null);
    try {
      const result = await Auth.inviteClient({
        email: trimmedEmail,
        name: name.trim(),
      });
      setSent({
        email: result?.email || trimmedEmail,
        resent: result?.resent === true,
      });
      setEmail("");
      setName("");
      if (typeof onInvited === "function") onInvited(result);
    } catch (err) {
      setSent(null);
      setError(errorMessage(err?.data?.error || "", trimmedEmail));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="crCard createClientAccount">
      <h3 className="crTitle">
        <CrIcon name="email" size={18} />
        Create a BossDrop client account
      </h3>
      <p className="muted" style={{ marginTop: 4 }}>
        Onboards a brand new customer company. This creates the{" "}
        <strong>owner account</strong> for that company's own BossDrop
        workspace, separate from yours, and emails them a link to set their
        password. It adds nobody to your workspace: to bring a colleague into
        the workspace you are signed in to, use <strong>Invite</strong> in Team
        Directory, in the Control Room.
      </p>

      <form className="createClientAccount__form" onSubmit={send} noValidate>
        <div className="crFieldRow">
          <label className="crField">
            <span className="crFieldLabel">New client's owner email</span>
            <input
              className="inputFancy"
              // type="text" and aria-required, not type="email" and `required`:
              // .inputFancy:invalid paints a red border, a half-typed address is
              // :invalid from the first keystroke, and an empty required field
              // is :invalid from first paint. This form checks the address
              // itself (EMAIL_RE) and prints its own message. inputMode keeps
              // the email keyboard on a phone.
              type="text"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              aria-required="true"
              autoComplete="off"
              placeholder="founder@theircompany.com"
              value={email}
              disabled={sending}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
            />
          </label>
          <label className="crField">
            <span className="crFieldLabel">Name (optional)</span>
            <input
              className="inputFancy"
              type="text"
              autoComplete="off"
              placeholder="Their name"
              value={name}
              disabled={sending}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
        </div>

        <div className="createClientAccount__actions">
          <button type="submit" className="btn primary" disabled={!canSend}>
            {sending ? "Creating..." : "Create account and send link"}
          </button>
        </div>

        {sent ? (
          <div className="muted small" role="status">
            {/* Nothing was created on a resend, so the chip must not claim it
                was: the account already existed, only the link is new. */}
            <span className="chip ok">
              {sent.resent ? "Link resent" : "Created"}
            </span>{" "}
            {sent.resent
              ? `${sent.email} already had an account waiting without a password, so a fresh link was sent.`
              : `Account created for ${sent.email}. Their link to set a password works for 7 days.`}
          </div>
        ) : null}

        {error ? (
          <div className="createClientAccount__error small" role="alert">
            {error}
          </div>
        ) : null}
      </form>
    </div>
  );
}
